import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  sidebarWidth,
  sidebarWidthCollapsed,
} from "~/components/layout/Sidebar.style";

const STORAGE_KEY = "desktop-sidebar-collapsed";

type DesktopSidebarContextValue = {
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
  toggleCollapsed: () => void;
  /** Pixel width of the fixed sidebar on desktop (tablet breakpoint and up). */
  sidebarWidthPx: number;
};

const DesktopSidebarContext = createContext<DesktopSidebarContextValue | null>(
  null
);

export const DesktopSidebarProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [collapsed, setCollapsedState] = useState(() => {
    try {
      return (
        typeof window !== "undefined" &&
        window.localStorage.getItem(STORAGE_KEY) === "1"
      );
    } catch {
      return false;
    }
  });

  const setCollapsed = useCallback((next: boolean) => {
    setCollapsedState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  const sidebarWidthPx = collapsed ? sidebarWidthCollapsed : sidebarWidth;

  const value = useMemo(
    () => ({
      collapsed,
      setCollapsed,
      toggleCollapsed,
      sidebarWidthPx,
    }),
    [collapsed, setCollapsed, toggleCollapsed, sidebarWidthPx]
  );

  return (
    <DesktopSidebarContext.Provider value={value}>
      {children}
    </DesktopSidebarContext.Provider>
  );
};

export function useDesktopSidebar(): DesktopSidebarContextValue {
  const ctx = useContext(DesktopSidebarContext);
  if (!ctx) {
    throw new Error(
      "useDesktopSidebar must be used within DesktopSidebarProvider"
    );
  }
  return ctx;
}
